import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Stack,
  Grid2,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Breadcrumbs,
  Link as MuiLink,
  Chip,
  Divider,
} from '@mui/material';
import { Iconify } from '../Minimal/src/components/iconify';
import { Link, useParams, useNavigate } from 'react-router-dom';
import config from "../common/Config";
import { toast } from "sonner";

const EditPurchasesFrom = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [supplierValue, setSupplierValue] = useState(null);
  const [productOptions, setProductOptions] = useState([]);
  const [productValue, setProductValue] = useState(null);
  const [referenceNo, setReferenceNo] = useState("");
  const token = localStorage.getItem('token');
  const accessToken = localStorage.getItem('accessToken');
  const roleID = localStorage.getItem("roleID");
  const [tableRows, setTableRows] = useState([]);
  const [deletedProducts, setDeletedProducts] = useState([]);
  const [note, setNote] = useState('');
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [netStdGoods, setNetStdGoods] = useState(0);
  const [netVatAmt, setNetVatAmt] = useState(0);
  const [netZeroGoods, setNetZeroGoods] = useState(0);
  const [netGrandTotal, setNetGrandTotal] = useState(0);
  const [selectedSupplierValue, setSelectedSupplierValue] = useState('');
  const [highlightedProductId, setHighlightedProductId] = useState(null);

  const company_tax_type = localStorage.getItem("company_tax_type");

  useEffect(() => {
    let stdGoods = 0;
    let totalVat = 0;
    let zeroGoods = 0;

    tableRows.forEach((productData) => {
      if (parseFloat(productData.rate) > 0) {
        stdGoods += parseFloat(productData.subtotal);
      }

      if (parseFloat(productData.rate) === 0.00) {
        zeroGoods += parseFloat(productData.subtotal);
      }

      totalVat += parseFloat(productData.vat);
    });

    setNetStdGoods(stdGoods);
    setNetVatAmt(totalVat);
    setNetZeroGoods(zeroGoods);

    const GrandTotal = stdGoods + totalVat + zeroGoods;
    setNetGrandTotal(GrandTotal);
  }, [tableRows]);

  const formatNumber = (num) => {
    return parseFloat(num).toFixed(2);
  };

  const fetchAddProductInfo = async (selectedOption) => {
    if (!selectedOption || !supplierValue) {
      toast.error('Please select a supplier first.');
      return;
    }

    const formData = new FormData();
    formData.append('product_id', selectedOption.id);
    formData.append('supplier', supplierValue.id);
    const existingProduct = tableRows.find(item => item.code === selectedOption.code || item.product_id === selectedOption.id);

    if (existingProduct) {
      setHighlightedProductId(existingProduct.id);
      toast.error('Product already exists in the list. Please update the quantity.');
      setTimeout(() => {
        setHighlightedProductId(null);
      }, 5000);
      setProductValue(null);
      return;
    } 

    const apiUrl = `${config.API_URL}/purchases/get_order_datails`;
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch new product');
      }

      const data = await response.json();
      const productData = data.data[0];
      const unitCost = productData.split === '0' || productData.split === '2'
        ? parseFloat(productData.cost)
        : parseFloat(productData.piece_cost);

      const onOrder = data.on_order_data[0]?.on_order_data || 0;
      const sales_avg_quantity = data.averageData[0].sales_avg_quantity;
      const number = parseFloat(sales_avg_quantity);
      const adjustedValue = (number % 1 >= 0.5) ? Math.ceil(number) : Math.floor(number);

      const stockCount = productData.split == '0' || productData.split == '2'
        ? parseInt(productData.quantity)
        : productData.split_quantity == null ? 0 : productData.split_quantity;

      const deficit = stockCount - parseInt(productData.sales_quantity) - parseInt(onOrder);

      const newRow = {
        code: productData.code,
        id: productData.id,
        pack: productData.pack,
        price: productData.price,
        piece_cost: productData.piece_cost,
        split: productData.split,
        type: productData.split === '0' || productData.split === '2' ? 'box' : 'piece',
        split_quantity: productData.split_quantity,
        split_price: productData.split_price,
        name: productData.name,
        size: productData.size,
        cost: productData.cost,
        rate: productData.rate,
        stockCount: stockCount,
        pdtcost: unitCost.toFixed(2),
        order_qty: 1,
        subtotal: (unitCost * 1).toFixed(2),
        vat: ((unitCost * 1) * formatNumber(productData.rate) / 100).toFixed(2),
        total: ((unitCost * 1) + ((unitCost * 1) * formatNumber(productData.rate) / 100)).toFixed(2),
        current_stock: productData.quantity,
        estimate: parseInt(productData.sales_quantity),
        product_id: productData.id,
        onOrder: onOrder,
        nextMonthEstimate: adjustedValue,
        deficit: deficit,
        item_type: productData.split === '0' || productData.split === '2' ? 'Box' : 'Piece',
        categorie_name: productData.categorie_name || productData.category_name || ''
      };

      setTableRows((prevRows) => [newRow,...prevRows]);
      setProductValue(null);
    } catch (error) {
      console.error('API fetch error:', error);
      toast.error('Failed to add product');
    }
  };

  const fetchSupplierList = async () => {
    const apiSalesRouteUrl = `${config.API_URL}/purchases/getSupplier`;
    try {
      const response = await fetch(apiSalesRouteUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${token}`,
        }
      });
      const data = await response.json();
      const formattedOptions = data.supplier.map(item => ({
        label: item.company,
        id: item.id,
      }));
      setSupplierOptions(formattedOptions);
    } catch (error) {
      console.error('api fail error fetching suppliers');
    }
  };

  const fetchProductList = async () => {
    const apiSalesRouteUrl = `${config.API_URL}/purchases/getProducts`;
    try {
      const response = await fetch(apiSalesRouteUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();
      const formattedOptions = data.products.map(item => ({
        label: `${item.name} (${item.code}) - ${item.size}`,
        id: item.id,
        code: item.code
      }));
      setProductOptions(formattedOptions);
    } catch (error) {
      console.error('api fail error fetching products');
    }
  };

  const fetchPurchesItemsList = async () => {
    const formData = new FormData();
    formData.append('id', orderId);
    const apiSalesRouteUrl = `${config.API_URL}/purchases/getpurches_items`;
    try {
      const response = await fetch(apiSalesRouteUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });
      const data = await response.json();
      const productData = data.purchase;
      const onOrderData = data.onOrderData;
      const averageData = data.averageData;
      setSelectedSupplierValue(data.commonData[0].supplierId);
      setReferenceNo(data.commonData[0].reference_number);

      const pdtRows = productData.map((item) => {
        const unitCost = item.split === '0' || item.split === '2'
          ? parseFloat(item.unit_cost)
          : parseFloat(item.piece_cost);

        const onOrder = onOrderData.find((i) => i.product_id === item.product_id);
        const averageOrderData = averageData.find((i) => i.product_id === item.product_id);

        const averageOrder = (parseFloat(averageOrderData.sales_avg_quantity).toFixed(2));
        const number = parseFloat(averageOrder);
        const adjustedValue = (number % 1 >= 0.5) ? Math.ceil(number) : Math.floor(number);
        const stockCount = item.split == '0' || item.split == '2' ? parseInt(item.current_stock) : parseInt(item.split_quantity);

        return {
          code: item.product_code,
          id: item.id,
          pack: item.pack,
          product_id: item.product_id,
          price: item.price,
          piece_cost: item.piece_cost,
          split: item.split,
          type: item.split === '0' || item.split === '2' ? 'box' : 'piece',
          item_type: item.type === '1' ? 'Box' : 'Piece',
          split_quantity: item.split_quantity,
          split_price: item.split_price,
          name: item.product_name,
          size: item.size,
          cost: item.unit_cost,
          rate: item.rate,
          stockCount: stockCount,
          pdtcost: unitCost.toFixed(2),
          order_qty: parseFloat(item.quantity),
          subtotal: (unitCost * parseFloat(item.quantity)).toFixed(2),
          vat: ((unitCost * parseFloat(item.quantity)) * (parseFloat(item.rate) / 100)).toFixed(2),
          total: ((unitCost * parseFloat(item.quantity)) + ((unitCost * parseFloat(item.quantity)) * (parseFloat(item.rate) / 100))).toFixed(2),
          estimate: parseInt(item.sales_quantity),
          onOrder: parseFloat(onOrder.on_order_data),
          nextMonthEstimate: adjustedValue,
          deficit: stockCount - parseInt(item.sales_quantity) - parseInt(onOrder.on_order_data),
          categorie_name: item.categorie_name || item.category_name || ''
        };
      });

      setTableRows(pdtRows);
    } catch (error) {
      console.error('api fail error fetching purchase items');
    }
  };

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    fetchSupplierList();
    fetchProductList();
    fetchPurchesItemsList();
  }, []);

  useEffect(() => {
    if (selectedSupplierValue && supplierOptions.length > 0) {
      const selectedCategory = supplierOptions.find(option => option.id == selectedSupplierValue);
      setSupplierValue(selectedCategory || null);
    }
  }, [selectedSupplierValue, supplierOptions]);

  const handleQuantityChange = (id, newQty) => {
    if (newQty === "" || newQty === null || newQty === undefined) {
      setTableRows(prevRows => {
        return prevRows.map(row => {
          if (row.id === id) {
            return { ...row, order_qty: "" };
          }
          return row;
        });
      });
      return;
    }

    if (!/^\d*\.?\d*$/.test(newQty)) {
      return;
    }

    const row = tableRows.find((r) => r.id === id);
    const isMeats = row && String(row.categorie_name || row.category_name || "").toUpperCase().trim() === "MEATS";
    if (!isMeats && String(newQty).includes(".")) {
      toast.error("Decimal quantity is only allowed for MEATS category.");
      return;
    }

    const numValue = parseFloat(newQty) || 0;
    if (isMeats) {
      const decLen = (String(newQty).split(".")[1] || "").length;
      if (decLen > 2) {
        toast.error("Quantity can have at most 2 decimal places.");
        return;
      }
      if (numValue > 9999.99) {
        toast.error("Limit: Maximum order quantity is 9999.99");
        return;
      }
    } else {
      if (numValue < 1) {
        return;
      }
      if (numValue > 9999.99) {
        toast.error("Limit: Maximum order quantity is 9999.99");
        return;
      }
    }

    const orderQtyToStore = isMeats ? numValue : (String(newQty).includes(".") ? parseInt(numValue, 10) : numValue);
    const orderQtyDisplay = isMeats ? newQty : String(orderQtyToStore);
    const orderQtyNum = isMeats ? (parseFloat(newQty) || 0) : orderQtyToStore;

    setTableRows(prevRows => {
      return prevRows.map(r => {
        if (r.id === id) {
          const unitCost = parseFloat(r.pdtcost) || 0;
          const rate = parseFloat(r.rate) || 0;
          const subtotal = (orderQtyNum * unitCost).toFixed(2);
          const vat = ((orderQtyNum * unitCost) * rate / 100).toFixed(2);
          const total = (parseFloat(subtotal) + parseFloat(vat)).toFixed(2);
          return {
            ...r,
            order_qty: orderQtyDisplay,
            subtotal,
            vat,
            total
          };
        }
        return r;
      });
    });
  };

  const handleCostChange = (id, newCost) => {
    if (newCost === "" || newCost === ".") {
      setTableRows(prevRows => {
        return prevRows.map(row => {
          if (row.id === id) {
            return {
              ...row,
              pdtcost: newCost
            };
          }
          return row;
        });
      });
      return;
    }

    if (!/^\d*\.?\d*$/.test(newCost)) {
      return;
    }

    const parts = newCost.split('.');
    if (parts.length > 1 && parts[1].length > 2) {
      return;
    }

    const numValue = parseFloat(newCost);
    if (isNaN(numValue)) {
      return;
    }

    if (numValue > 9999.99) {
      toast.error("Limit: Maximum cost price is 9999.99");
      return;
    }

    setTableRows(prevRows => {
      return prevRows.map(row => {
        if (row.id === id) {
          const order_qty = parseFloat(row.order_qty) || 0;
          const rate = parseFloat(row.rate) || 0;
          const subtotal = (order_qty * numValue).toFixed(2);
          const vat = ((order_qty * numValue) * rate / 100).toFixed(2);
          const total = (parseFloat(subtotal) + parseFloat(vat)).toFixed(2);
          return {
            ...row,
            pdtcost: newCost,
            subtotal,
            vat,
            total
          };
        }
        return row;
      });
    });
  };

  const handleOptionChange = (id, pdtType) => {
    setTableRows(prevRows => {
      return prevRows.map(row => {
        if (row.id === id) {
          const pdtcost = pdtType === 'Box' ? row.cost : row.piece_cost;
          const order_qty = parseFloat(row.order_qty) || 0;
          const rate = parseFloat(row.rate) || 0;
          const subtotal = (order_qty * pdtcost).toFixed(2);
          const vat = ((order_qty * pdtcost) * rate / 100).toFixed(2);
          const total = (parseFloat(subtotal) + parseFloat(vat)).toFixed(2);
          const type = pdtType === 'Box' ? 'box' : 'piece';
          const item_type = pdtType === 'Box' ? 'Box' : 'Piece';
          const stockCount = pdtType == 'Box' ? parseInt(row.current_stock || 0) : parseInt(row.split_quantity !== null ? row.split_quantity : 0);
          const deficit = parseInt(stockCount) - parseInt(row.estimate) - parseInt(row.onOrder);

          return {
            ...row,
            type,
            pdtcost,
            subtotal,
            vat,
            item_type,
            total,
            stockCount,
            deficit
          };
        }
        return row;
      });
    });
  };

  const handleCancel = (product_id) => {
    setTableRows(prevRows => prevRows.filter(row => row.product_id !== product_id));
    setDeletedProducts(prevProducts => [...prevProducts, product_id]);
    toast.success('Product removed from list');
  };

  const submitFormData = async () => {
    if (tableRows.length === 0) {
      toast.warning("Please add at least one product in purchase.");
      return;
    }

    if (!supplierValue) {
      toast.warning("Please select a supplier.");
      return;
    }

    const formData = new FormData();
    formData.append('supplier', supplierValue.id);
    formData.append('purchase_id', orderId);
    formData.append('note', note);
    formData.append('reference_number', referenceNo);

    tableRows.forEach((row) => {
      formData.append('product_id[]', row.product_id);
      formData.append('code[]', row.code);
      formData.append('name[]', row.name);
      formData.append('order_qty[]', row.order_qty);
      formData.append('type[]', row.type);
      formData.append('cost[]', row.pdtcost);
      formData.append('subtotal[]', row.subtotal);
      formData.append('vat[]', row.vat);
      formData.append('total[]', row.total);
    });

    deletedProducts.forEach(id => {
      formData.append('deletedProducts[]', id);
    });

    const apiSalesRapUrl = `${config.API_URL}/purchases/update_purchase`;
    try {
      const response = await fetch(apiSalesRapUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (data.success == true) {
        toast.success("Purchase Updated successfully.");
        setTimeout(() => {
          navigate('/purchases/purchases_order/');
        }, 1500);
      } else {
        toast.error("Purchase Updated Failed");
      }
    } catch (error) {
      console.error('Error fetching api:', error);
      toast.error("Failed to update purchase");
    }
  };

  const handleDeletePurchase = () => {
    setDeleteConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteConfirmDialog(false);
    try {
      const apiDeleteUrl = `${config.API_URL}/purchases/delete_purchase`;
      const formData = new FormData();
      formData.append('purchase_id', orderId);
      const response = await fetch(apiDeleteUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });
      const data = await response.json();
      if (data.success === true) {
        toast.success("Purchase order deleted successfully.");
        setTimeout(() => {
          navigate('/purchases/purchases_order/');
        }, 1500);
      } else {
        toast.error("Failed to delete purchase order.");
      }
    } catch (error) {
      toast.error("An error occurred while deleting.");
    }
  };

  const textLableStyle = {
    fontWeight: 500,
    mt: 0.5,
    fontSize: "1rem",
    color: 'text.secondary'
  };
  const textValueStyle = {
    fontWeight: 500,
    mt: 0.5,
    fontSize: "1rem",
    color: 'primary.main'
  };

  return (
    <>
      <Box sx={{ backgroundColor: '#f5f7fa', p: 1.5 }}>
        <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid rgba(145, 158, 171, 0.16)' }}>
          <Box sx={{ p: 2 }}>
            <Grid2 container spacing={2} display="flex" justifyContent="space-between" alignItems="center" gap={2}>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <Typography className="page-title">
                  Edit Purchase Order
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <Autocomplete
                  options={supplierOptions}
                  className="input-border"
                  getOptionLabel={(option) => option.label || ''}
                  value={supplierValue}
                  onChange={(event, newValue) => {
                    setSupplierValue(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Supplier" size="small"
                      sx={{
                        '& .MuiFormLabel-root': {
                          top: '-2px'
                        },
                        '& .MuiFormLabel-root.Mui-focused': {
                          top: '0px'
                        }
                      }}
                    />
                  )}
                  sx={{ width: '100%' }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <Autocomplete
                  options={productOptions}
                  className="input-border"
                  getOptionLabel={(option) => option.label || ''}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  value={productValue}
                  onChange={(event, newValue) => {
                    setProductValue(newValue);
                    if (newValue) {
                      fetchAddProductInfo(newValue);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params}
                      label="Select Product"
                      size="small"
                      sx={{
                        '& .MuiFormLabel-root': {
                          top: '-2px'
                        },
                        '& .MuiFormLabel-root.Mui-focused': {
                          top: '0px'
                        }
                      }}
                    />
                  )}
                  sx={{ width: '100%' }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 3 }}>
                <TextField
                  className="input-border"
                  fullWidth
                  size="small"
                  label="Reference No"
                  value={referenceNo}
                  onChange={(e) => setReferenceNo(e.target.value)}
                  sx={{
                    '& .MuiFormLabel-root': {
                      top: '-2px'
                    },
                    '& .MuiFormLabel-root.Mui-focused': {
                      top: '0px'
                    }
                  }}
                />
              </Grid2>
            </Grid2>
          </Box>

          <Paper elevation={0} sx={{ borderRadius: 0, border: '1px solid rgba(145, 158, 171, 0.16)', overflow: 'hidden', mb: 2 }}>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table size="small" className="table-cell-border">
                <TableHead>
                  <TableRow className="table-cell-header">
                    <TableCell width="220px">Code-Name-Size</TableCell>
                    <TableCell width="50px" align="center">PK</TableCell>
                    <TableCell width="100px" align="center">Current Stock</TableCell>
                    <TableCell width="90px" align="center">
                      On Order
                      <Tooltip title="PO Qty with Draft or Partial status" arrow>
                        <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
                          <Iconify icon="eva:info-outline" width={16} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell width="80px" align="center">
                      Estimated
                      <Tooltip title="Avg 2 month sell" arrow>
                        <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
                          <Iconify icon="eva:info-outline" width={16} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell width="100px" align="center" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                      Deficit
                      <Tooltip title="Current stock - On order Stock - Estimate" arrow>
                        <IconButton size="small" sx={{ ml: 0.5, p: 0 }}>
                          <Iconify icon="eva:info-outline" width={16} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell width="110px" align="center">Order QTY</TableCell>
                    <TableCell width="91px" align="center">Type</TableCell>
                    <TableCell width="91px" align="right">Cost Price</TableCell>
                    <TableCell width="80px" align="right" >{company_tax_type}</TableCell>
                    <TableCell width="80px" align="right">Sub Total</TableCell>
                    <TableCell width="80px" align="right">Net P</TableCell>
                    <TableCell width="50px" align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No product added</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableRows.map((row) => {
                      const isHighlighted = highlightedProductId === row.id;
                      return (
                      <TableRow key={row.id} hover
                        sx={{
                          backgroundColor: isHighlighted ? "#cfe2ff" : "transparent",
                          transition: "background-color 0.3s ease",
                          "&:hover": {
                            backgroundColor: isHighlighted ? "#cfe2ff" : "rgba(145, 158, 171, 0.08)",
                          },
                          '& .MuiTableCell-root': {
                            fontSize: '0.75rem',
                            color: 'text.primary',
                            py: 1,
                          }
                        }}
                      >
                        <TableCell>{row.code} - {row.name} - {row.size}</TableCell>
                        <TableCell align="center">{row.pack}</TableCell>
                        <TableCell align="center">{row.stockCount}</TableCell>
                        <TableCell align="center">{row.onOrder}</TableCell>
                        <TableCell align="center">{row.estimate}</TableCell>
                        <TableCell align="center">{row.deficit}</TableCell>
                        <TableCell align="center">
                          <TextField
                            className="input-border"
                            type="text"
                            value={row.order_qty}
                            onChange={(e) => handleQuantityChange(row.id, e.target.value)}
                            onKeyDown={(e) => {
                              if (['e', 'E', '+', '-', ','].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            size="small"
                            fontSize="0.75rem"
                            inputProps={{ style: { textAlign: 'center', height: '12px', width: '70px' } }}
                            sx={{ width: '80px' }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={{ px: 0 }}>
                          {row.split == 2 ? (
                            <FormControl size="small" sx={{ minWidth: 100 }}>
                              <Select
                                size="small"
                                value={row.item_type}
                                onChange={(e) => handleOptionChange(row.id, e.target.value)}
                                sx={{ fontSize: '0.75rem' }}

                              >
                                <MenuItem value="Box">Box</MenuItem>
                                <MenuItem value="Piece">Piece</MenuItem>
                              </Select>
                            </FormControl>
                          ) : (
                            <Typography sx={{ fontSize: '0.75rem' }}>
                              {row.split == 0 ? 'Box' : 'Piece'}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell >
                          <TextField
                          className="input-border"
                            size="small"
                            type="text"
                            value={row.pdtcost}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || /^\d*\.?\d*$/.test(value)) {
                                handleCostChange(row.id, value);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (['e', 'E', '+', '-'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            fontSize="0.75rem"
                            inputProps={{ style: { textAlign: 'right', height: '12px'} }}
                            disabled={roleID === '20'}
                            sx={{ width: '83px' }}
                          />
                        </TableCell>
                        <TableCell align="right">{row.vat}</TableCell>
                        <TableCell align="right">{row.subtotal}</TableCell>
                        <TableCell align="right">{row.total}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleCancel(row.product_id)}
                            color="error"
                          >
                            <Iconify icon="eva:trash-2-outline" width={20} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper elevation={0} sx={{ px: 2 }}>
            <Grid2 container spacing={2} display="flex" justifyContent="space-around" alignItems="center" gap={2}>
              <Grid2 size={{ xs: 12, md: 12 }}>
                <Box border="1px solid rgba(16, 20, 24, 0.55)" borderRadius={1} p={1} display="flex" justifyContent="space-between" alignItems="center" gap={1} py={1.5}>
                  <Grid2 size={{ xs: 6, md: 3 }} gap={1} display="flex" alignItems="center" justifyContent="center">
                    <Typography variant="body2" color="text.secondary">VAT Goods :</Typography>
                    <Typography sx={{ fontWeight: 500, mt: 0.5, fontSize: "1rem" }}>
                      {netStdGoods.toFixed(2)}
                    </Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 6, md: 3 }} gap={1} display="flex" alignItems="center" justifyContent="center">
                    <Typography variant="body2" color="text.secondary">{company_tax_type} Amount :</Typography>
                    <Typography sx={{ fontWeight: 500, mt: 0.5, fontSize: "1rem" }}>
                      {netVatAmt.toFixed(2)}
                    </Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 6, md: 3 }} gap={1} display="flex" alignItems="center" justifyContent="center">
                    <Typography variant="body2" color="text.secondary">Zero VAT Goods :</Typography>
                    <Typography sx={{ fontWeight: 500, mt: 0.5, fontSize: "1rem" }}>
                      {netZeroGoods.toFixed(2)}
                    </Typography>
                  </Grid2>
                  <Grid2 size={{ xs: 6, md: 3 }} gap={1} display="flex" alignItems="center" justifyContent="center">
                    <Typography variant="body2" color="text.secondary">Gross Total :</Typography>
                    <Typography sx={{ fontWeight: 500, mt: 0.5, color: 'primary.main', fontSize: "1rem" }}>
                      {netGrandTotal.toFixed(2)}
                    </Typography>
                  </Grid2>
                </Box>
              </Grid2>

            </Grid2>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={1}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a note..."
                sx={{
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#D0D5DD !important",
                  },
                }}
              />
            </Box>
          </Paper>

          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ m: 2 }}>
            <Button
              variant="outlined"
              color="error"
              onClick={handleDeletePurchase}
              startIcon={<Iconify icon="eva:trash-2-outline" />}
            >
              Delete Purchase Order
            </Button>
            <Button
              variant="contained"
              className="button-variant-contained"
              onClick={submitFormData}
              startIcon={<Iconify icon="eva:save-outline" />}
            >
              Update Purchase Order
            </Button>
          </Stack>
        </Paper>
      </Box>

      <Dialog
        open={deleteConfirmDialog}
        onClose={() => setDeleteConfirmDialog(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this purchase order? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditPurchasesFrom;
